import React, { useState, useEffect } from "react";
import InventoryItem from "./InventoryItem";
import { useGameStateContext } from "../GameStateProvider";
import { useBrewingActions } from "../hooks/useBrewingActions";
import { CONSTANTS } from "../data";

const InventoryPanel: React.FC = () => {
  const { state, dispatch } = useGameStateContext();
  const { handleSelectItem } = useBrewingActions();
  const activeTab = state.inventoryActiveTab || 'ingredients';
  const setActiveTab = (tab: string) => dispatch({ type: 'SET_INVENTORY_TAB', tab });

  useEffect(() => {
    const { isTutorialActive, currentTutorial } = state.tutorialState;
    if (isTutorialActive && currentTutorial) {
      const tutorialDef = CONSTANTS.TUTORIALS[currentTutorial.id];
      if (!tutorialDef) return;
      const currentStepInfo = tutorialDef.steps[currentTutorial.step];
      if (!currentStepInfo || !currentStepInfo.action || currentStepInfo.action.type !== 'selectItem') {
        return;
      }
      
      const itemId = currentStepInfo.action.itemId;
      if (!itemId) return;

      const item = CONSTANTS.ITEMS[itemId] || Object.values(CONSTANTS.RECIPES).find(r => r.result.id === itemId)?.result;
      if (!item) return;

      if (CONSTANTS.INVENTORY_CATEGORIES.INGREDIENTS.types.includes(item.type)) {
        setActiveTab('ingredients');
      } else if (CONSTANTS.INVENTORY_CATEGORIES.TOOLS.types.includes(item.type)) {
        setActiveTab('tools');
      } else if (CONSTANTS.INVENTORY_CATEGORIES.DRINKS.types.includes(item.type)) {
        setActiveTab('drinks');
      }
    }
  }, [state.tutorialState, dispatch]);

  // Get inventory items for display
  const inventoryItems = Object.entries(state.inventory)
    .filter(([id, quantity]) => quantity > 0)
    .map(([id]) => {
      const item = CONSTANTS.ITEMS[id];
      if (item) {
        return { ...item, id };
      }
      // Check recipes
      const recipe = Object.values(CONSTANTS.RECIPES).find(r => r.result.id === id);
      return recipe ? recipe.result : null;
    })
    .filter(Boolean);

  const selectedInStations = state.brewStations.flatMap(s => s.slots).filter(Boolean);

  // Categorize items based on their type
  const categorizedItems = {
    ingredients: inventoryItems.filter(item => 
      CONSTANTS.INVENTORY_CATEGORIES.INGREDIENTS.types.includes(item.type)
    ),
    tools: inventoryItems.filter(item => 
      CONSTANTS.INVENTORY_CATEGORIES.TOOLS.types.includes(item.type)
    ),
    drinks: inventoryItems.filter(item => 
      CONSTANTS.INVENTORY_CATEGORIES.DRINKS.types.includes(item.type)
    )
  };

  const tabs = [
    { id: 'ingredients', name: CONSTANTS.INVENTORY_CATEGORIES.INGREDIENTS.name, count: categorizedItems.ingredients.length },
    { id: 'tools', name: CONSTANTS.INVENTORY_CATEGORIES.TOOLS.name, count: categorizedItems.tools.length },
    { id: 'drinks', name: CONSTANTS.INVENTORY_CATEGORIES.DRINKS.name, count: categorizedItems.drinks.length }
  ];

  return (
    <aside
      data-tutorial-id="inventory-panel"
      className="bg-surface p-2 sm:p-4 flex flex-col h-full mobile-inventory-panel"
    >
      <h3 className="font-bold text-lg sm:text-xl mb-2 sm:mb-4 flex-shrink-0 text-text-primary">Inventory</h3>
      
      {/* Tab Navigation */}
      <div className="flex mb-2 sm:mb-4 border-b border-border-color flex-shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-1.5 sm:py-2 px-1 sm:px-2 text-xs sm:text-sm font-semibold transition-colors relative ${
              activeTab === tab.id
                ? 'text-primary-action'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <span className="block sm:hidden">{tab.name.slice(0, 4)}</span>
            <span className="hidden sm:block">{tab.name}</span>
            {tab.count > 0 && (
              <span className={`ml-1 text-xs font-normal`}>
                ({tab.count})
              </span>
            )}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-action"></div>
            )}
          </button>
        ))}
      </div>

      {/* Items Grid */}
      <div className="flex-1 overflow-y-auto pr-1 sm:pr-2 -mr-1 sm:-mr-2">
        <div className="inventory-grid grid grid-cols-3 sm:grid-cols-3 gap-1.5 sm:gap-2">
          {categorizedItems[activeTab].map((item) => (
            <InventoryItem
              key={item.id}
              item={item}
              quantity={state.inventory[item.id]}
              selected={selectedInStations.includes(item.id)}
              onSelect={handleSelectItem}
            />
          ))}
        </div>
        
        {/* Empty state */}
        {categorizedItems[activeTab].length === 0 && (
          <div className="text-center text-text-secondary py-4 sm:py-8">
            <div className="text-xs sm:text-sm">
              No {tabs.find(t => t.id === activeTab)?.name.toLowerCase()} available
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default InventoryPanel;
